import { Card, CardContent } from "@/components/ui/card";

/**
 * Editorial long-form content (~1000 words) for high-priority model pages.
 * Auto-selects by make+model slug. Falls back to null for non-covered models.
 * Injected in MakeModelPage after ModelStatsContent for RO pages.
 */

type Section = { h: string; p: string };
type Editorial = { intro: string; sections: Section[] };

const EDITORIALS: Record<string, Editorial> = {
  "volkswagen/golf-6": {
    intro:
      "Volkswagen Golf 6 rămâne, în 2026, una dintre cele mai căutate mașini second-hand din România. Motivul e simplu: raport calitate-preț solid, piese de schimb disponibile la orice colț și un compromis rezonabil între confort, consum și fiabilitate. Fabricat între 2008 și 2013, Golf 6 se găsește într-o gamă largă de motorizări și dotări, ceea ce înseamnă că poți alege între o variantă entry-level pentru oraș și un GTI sau GTD pentru cine vrea mai mult caracter.",
    sections: [
      {
        h: "Cât costă un Volkswagen Golf 6 în 2026",
        p: "Prețurile pentru un Golf 6 în stare bună variază, în general, între 4.500 € și 9.500 €, în funcție de motorizare, an, kilometraj și istoric. Un 1.6 TDI cu 105 CP din 2010-2012, cu 200-250.000 km reali și istoric documentat, se așează undeva la 5.500-7.000 €. Un 2.0 TDI cu 140 CP, mai râvnit, urcă spre 7.500-9.000 €. Versiunile 1.4 TSI (benzină, 122 CP) sunt bune de oraș, dar au capitolul lanț de distribuție care trebuie verificat. GTI și GTD-urile, mai rare, pornesc de la 9.000 € și pot depăși 13.000 € pentru exemplare cu istoric curat.",
      },
      {
        h: "Ce motor să alegi: diesel, benzină sau GTI",
        p: "Cel mai popular rămâne 1.6 TDI (CAYC / CAYB) — economic (5-6 l/100 km real), rezistent, cu piese ieftine, dar cu grijă la EGR, valve turbo și injectoare Bosch care încep să dea semne peste 250.000 km. 2.0 TDI (CFHC / CBDC) e mai suplu, dar necesită atenție la volanta bimasă și DPF. Pe benzină, 1.4 TSI cu twincharger (CAVA) e sensibil la lanț de distribuție înainte de 2011; 1.4 TSI 122 CP e mai calm. Pentru cine caută condus real, 2.0 TSI din GTI (211 CP) e alegerea, dar e semnificativ mai scump la întreținere și consumă 8-10 l/100 km.",
      },
      {
        h: "Probleme comune la Golf 6 pe care trebuie să le verifici",
        p: "1) Cutia DSG (DQ200, 7 trepte, uscată) — smucituri la pornire, mecatronic sensibil, ulei la 60.000 km obligatoriu. 2) Lanț de distribuție la 1.4 TSI pre-2011 — zgomot metalic la rece = risc de sărire a lanțului. 3) EGR și DPF la diesel — colmatare frecventă dacă mașina a fost folosită doar în oraș. 4) Volantă bimasă la 2.0 TDI — vibrații la ralanti = 500-800 € înlocuire. 5) Blocuri de siguranțe / geamuri electrice — falsuri contacte după 200.000 km. 6) Rugină pe pragurile spate și marginea aripilor la exemplare din nord. Un test drive de 30 de minute cu OBD conectat rezolvă 80% din surprize.",
      },
      {
        h: "Golf 6 vs Golf 5 vs Golf 7: care e diferența reală",
        p: "Față de Golf 5, Golf 6 aduce insonorizare mai bună, interior mai curat și motorizări TDI common-rail (mai fiabile decât PD-urile de pe Golf 5). Față de Golf 7, Golf 6 e cu 2-3.000 € mai ieftin la același an și kilometraj, dar Golf 7 câștigă la consum (7 e mai ușor cu ~100 kg), la infotainment și la siguranță activă. Dacă bugetul e sub 7.000 €, Golf 6 e alegerea logică. Peste 8.500 €, uită-te la Golf 7 1.6 TDI din 2013-2014.",
      },
      {
        h: "Ce să ceri vânzătorului: checklist rapid",
        p: "Istoric service complet (idealul: carnet + facturi), raport carVertical sau autoDNA, ultima schimbare de ulei la cutie (dacă e DSG), stare curea/lanț distribuție, măsurare geometrie, foto sub caroserie pe elevator. Cere VIN-ul și verifică-l gratuit pe RAR + istoric daune. Dacă vânzătorul refuză un test la service ales de tine (100-150 lei), pleacă. Pe RoAutoMarket, dealerii verificați RISCO au istoric fiscal la zi — filtrează după badge-ul verificat pentru mai puțină bătaie de cap.",
      },
    ],
  },
};

interface Props {
  make: string;
  model?: string;
}

export const ModelEditorialContent = ({ make, model }: Props) => {
  if (!model) return null;
  const key = `${make.toLowerCase()}/${model.toLowerCase()}`;
  const ed = EDITORIALS[key];
  if (!ed) return null;

  return (
    <section className="mb-12" aria-label="Ghid detaliat model">
      <Card>
        <CardContent className="p-6 md:p-8 prose prose-sm md:prose-base max-w-none dark:prose-invert">
          <p className="lead">{ed.intro}</p>
          {ed.sections.map((s) => (
            <div key={s.h} className="mt-6">
              <h2 className="text-xl md:text-2xl font-semibold mb-2">{s.h}</h2>
              <p className="text-muted-foreground leading-relaxed">{s.p}</p>
            </div>
          ))}
        </CardContent>
      </Card>
    </section>
  );
};

export default ModelEditorialContent;
